import { Layout as AntLayout } from 'antd';
import { Suspense, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { HEADER_HEIGHT, SIDER_WIDTH, SIDER_WIDTH_COLLAPSE } from '../../constants/layout';
import { useIsMobile } from '../../hooks/useIsMobile';
import { cn } from '../../lib/utils';

import Header from './components/Header';
import LoadingFallback from './components/LoadingFallback';
import Sidebar from './components/Sidebar';
import type { LayoutContextType } from './types';
import type { BreadcrumbItem } from '../ui/Breadcrumb/Breadcrumb';

const { Content } = AntLayout;

// Matches Sidebar's own `breakpoint="lg"` — below this, Sidebar treats
// itself as "narrow" and we switch it from pushing content aside to
// floating over it, since shoving 250px off a phone-sized screen would
// leave almost nothing for the header/content underneath.
const NARROW_SCREEN_QUERY = '(max-width: 991px)';

/**
 * Main Layout component
 * Provides a collapsible sidebar, header, and content area
 * Uses Outlet Context to allow pages to set header title and breadcrumb items
 */
export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [headerTitle, setHeaderTitle] = useState('');
  const [breadcrumbItems, setBreadcrumbItems] = useState<BreadcrumbItem[]>([]);
  const isNarrowScreen = useIsMobile(NARROW_SCREEN_QUERY);

  const handleToggleSidebar = () => {
    setCollapsed((prev) => !prev);
  };

  const outletContext: LayoutContextType = {
    setHeaderTitle,
    setBreadcrumbItems,
  };

  // On narrow screens the sidebar overlays the content instead of pushing
  // it, so the header/content offset stays pinned at the collapsed width
  // even while the sidebar itself is visually expanded.
  const contentOffset = collapsed || isNarrowScreen ? SIDER_WIDTH_COLLAPSE : SIDER_WIDTH;
  const sidebarOverlaying = isNarrowScreen && !collapsed;

  return (
    <AntLayout className="min-h-screen w-full max-w-full overflow-x-hidden">
      <Sidebar collapsed={collapsed} onToggle={handleToggleSidebar} />
      {sidebarOverlaying && (
        <div
          className="fixed inset-0 z-35 bg-black/40 transition-opacity duration-200"
          onClick={handleToggleSidebar}
          aria-hidden="true"
        />
      )}
      <AntLayout
        className={cn('min-w-0 max-w-full overflow-x-hidden transition-all duration-200')}
        style={{
          paddingLeft: contentOffset,
        }}
      >
        <Header breadcrumbItems={breadcrumbItems} title={headerTitle} sidebarOffset={contentOffset} />
        <Content
          className="min-w-0 overflow-x-hidden bg-white!"
          style={{ minHeight: `calc(100vh - ${HEADER_HEIGHT}px)` }}
        >
          <Suspense fallback={<LoadingFallback />}>
            <Outlet context={outletContext} />
          </Suspense>
        </Content>
      </AntLayout>
    </AntLayout>
  );
}
